import { useEffect, useState, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Box, Container, Toolbar, LinearProgress, Alert, AlertColor } from '@mui/material';
import { Id, toast } from 'react-toastify';

import Drawer from './Drawer';
import Header from './Header';
import Breadcrumbs from 'components/common/Breadcrumbs/Breadcrumbs';
import useConfig from 'hooks/useConfig';
import { dispatch } from 'store';
import { openDrawer } from 'store/reducers/menu';
import { MenuOrientation } from 'types/config';
import { IS_UNDER_CONSTRUCTION } from 'config';
import {
    useGetApiUserInfoWithHeadersQuery,
    useGetApiUserPreferencesParsedQuery,
    useGetApiProjectResourceQuery,
} from 'store/userOrg/enhancedUserOrgApi';
import { error500Path } from 'routes/paths';
import { QpuStatusAlerts } from './QpuStatusAlerts';
import BeginTour from 'components/features/welcome/BeginTour';
import useNetworkStatus from 'hooks/useNetworkStatus';
import UnderConstruction from 'pages/Maintenance/UnderConstruction';
import { throwSuccessToast } from 'utils';
import { errorMessages, successMessages } from 'constants/messages';
import Loader from 'components/common/Loader/Loader';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';

const DRAWER_WIDTH = '260px';
const HEADER_HEIGHT_OFFSET = '110px';

const MainLayout = () => {
    const isUnderConstruction = IS_UNDER_CONSTRUCTION;
    const isOnline = useNetworkStatus();

    const wasOffline = useRef(false);
    const toastId = useRef<Id | undefined>(undefined);

    const {
        isLoading: userIsLoading,
        isError: userIsError,
        isSuccess: userIsSuccess,
        data: userData,
        error: userError,
    } = useGetApiUserInfoWithHeadersQuery(undefined, { skip: isUnderConstruction });

    const { data: projectResources } = useGetApiProjectResourceQuery({}, { skip: isUnderConstruction });

    const theme = useTheme();
    const [skipGePreferences, setSkipGePreferences] = useState(true);
    const matchDownXL = useMediaQuery(theme.breakpoints.down('xl'));
    const downLG = useMediaQuery(theme.breakpoints.down('lg'));
    const navigate = useNavigate();

    const {
        container,
        miniDrawer,
        menuOrientation,
        onChangeMode,
        onChangePresetColor,
        onChangeFontFamily,
        onChangeFirstStart,
        onChangeTableOptions,
    } = useConfig();

    const {
        data: preferences,
        isSuccess: preferencesIsSuccess,
        isLoading: preferencesIsLoading,
        isError: preferencesIsError,
        error: preferencesError,
    } = useGetApiUserPreferencesParsedQuery(undefined, { skip: skipGePreferences || isUnderConstruction });

    const checkIsUnauthorized = (error: FetchBaseQueryError | SerializedError | undefined): boolean => {
        if (!error) return false;

        const hasStatus401 = 'status' in error && error.status === 401;
        const hasOriginalStatus401 = 'originalStatus' in error && error.originalStatus === 401;

        return hasStatus401 || hasOriginalStatus401;
    };

    const isUnauthorized = checkIsUnauthorized(userError) || checkIsUnauthorized(preferencesError);
    useEffect(() => {
        if (preferencesIsSuccess && preferences) {
            if (preferences.presetColor) onChangePresetColor(preferences.presetColor);
            if (preferences.mode) onChangeMode(preferences.mode);
            if (preferences.fontFamily) onChangeFontFamily(preferences.fontFamily);
            if (preferences.firstStart) onChangeFirstStart(preferences.firstStart);
            if (preferences.tableOptions) onChangeTableOptions(preferences.tableOptions);
        }
    }, [
        preferencesIsSuccess,
        preferences,
        onChangeMode,
        onChangePresetColor,
        onChangeFontFamily,
        onChangeFirstStart,
        onChangeTableOptions,
    ]);

    const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

    useEffect(() => {
        if (!isUnauthorized && (preferencesIsError || userIsError)) {
            navigate(error500Path(true));
        }
        if (userIsSuccess) {
            setSkipGePreferences(false);
        }
    }, [userIsError, preferencesIsError, userIsSuccess, isUnauthorized, navigate]);

    useEffect(() => {
        if (!miniDrawer) {
            dispatch(openDrawer(!matchDownXL));
        }
    }, [matchDownXL, miniDrawer]);

    useEffect(() => {
        if (!isOnline) {
            if (!toastId.current) {
                toastId.current = toast.error(errorMessages.offline, {
                    autoClose: false,
                    closeOnClick: false,
                    draggable: false,
                    position: toast.POSITION.TOP_CENTER,
                    closeButton: false,
                    theme: 'colored',
                });
            }
            wasOffline.current = true;
        } else if (wasOffline.current) {
            if (toastId.current) {
                toast.dismiss(toastId.current);
                toastId.current = undefined;
            }
            throwSuccessToast({ message: successMessages.backOnline });
            wasOffline.current = false;
        }
    }, [isOnline]);

    if (isUnderConstruction) {
        return <UnderConstruction />;
    }

    const renderMainContent = () => {
        if (isUnauthorized) return <Loader />;
        if (userIsLoading || preferencesIsLoading) {
            return <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 9999 }} />;
        }

        return (
            <>
                <Header />
                <Drawer />
                {preferences && <BeginTour />}
                <Box component="main" sx={{ width: `calc(100% - ${DRAWER_WIDTH})`, flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                    <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit' }} />
                    <Container
                        maxWidth={container ? 'xl' : false}
                        sx={{
                            ...(container && { px: { xs: 0, sm: 2 } }),
                            position: 'relative',
                            minHeight: `calc(100vh - ${HEADER_HEIGHT_OFFSET})`,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {userData?.headers?.alertmessage && userData?.headers?.alertstatus && (
                            <Alert severity={userData.headers.alertstatus as AlertColor} sx={{ mb: 2 }}>
                                {userData.headers.alertmessage}
                            </Alert>
                        )}
                        <QpuStatusAlerts projectResources={projectResources} />
                        <Breadcrumbs />
                        <Outlet />
                    </Container>
                </Box>
            </>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                opacity: !isOnline ? 0.5 : 1,
                pointerEvents: !isOnline ? 'none' : 'auto',
            }}
        >
            {renderMainContent()}
        </Box>
    );
};

export default MainLayout;